import React, { useState, useEffect, useCallback, useMemo } from "react";
import { CartContext } from "./CartContext";
import { getCartCount, getCartItems } from "../services/Cart";
import { getWallet } from "../services/Spares";
import {
  calculateCartPurchaseData,
  getSavedCartPurchaseData,
  removeSavedCartPurchaseData,
  saveCartPurchaseData,
} from "../utils/purchaseOptions";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [productIds, setProductIds] = useState([]);
  const [itemList, setItemList] = useState([]);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [userId, setUserId] = useState("");
  const [walletAmount, setWalletAmount] = useState("0.00");

  useEffect(() => {
    const id = localStorage.getItem("userId");
    if (id) setUserId(id);
  }, []);

  useEffect(() => {
    if (!userId) return;
    const fetchData = async () => {
      try {
        const response = await getWallet();
        const data = Array.isArray(response) ? response : response?.data;
        console.log("wallet fetched:", data?.[0]?.amount);
        setWalletAmount(data?.[0]?.amount || "0.00");
      } catch (err) {
        console.log(err);
        setWalletAmount(0);
      }
    };

    fetchData();
  }, [userId,items]);

  const fetchCart = useCallback(async () => {
    if (!userId) return;

    try {
      const response = await getCartItems();
      setItems(response?.Data || []);
      console.log(response);
    } catch (err) {
      console.error("Cart fetch failed:", err);
    }
  }, [userId]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const setCartQty = useCallback((productid, qty, purchaseType = "piece") => {
    const pid = String(productid);
    const nextQty = Number(qty) || 0;

    setItems((prev) =>
      prev
        .map((item) =>
          String(item.dtl?.[0]?.productid || item.id) === pid
            ? { ...item, qty: nextQty }
            : item,
        )
        .filter((item) => Number(item.qty) > 0),
    );

    setItemList((prev) => {
      const exists = prev.some((p) => String(p.productId) === pid);
      if (!exists && nextQty > 0) {
        return [...prev, { productId: productid, count: nextQty }];
      }

      return prev
        .map((p) =>
          String(p.productId) === pid ? { ...p, count: nextQty } : p,
        )
        .filter((p) => Number(p.count) > 0);
    });

    if (nextQty > 0) {
      const savedPurchaseData = getSavedCartPurchaseData(productid);
      const nextPurchaseData = savedPurchaseData
        ? {
            ...savedPurchaseData,
            quantity: nextQty,
            discountAmount:
              (Number(savedPurchaseData.unitPrice || 0) *
                nextQty *
                Number(savedPurchaseData.discountPercentage || 0)) /
              100,
            finalPrice:
              Number(savedPurchaseData.unitPrice || 0) *
              nextQty *
              (1 - Number(savedPurchaseData.discountPercentage || 0) / 100),
          }
        : calculateCartPurchaseData({ productid }, purchaseType, nextQty);

      saveCartPurchaseData(nextPurchaseData);
    } else {
      removeSavedCartPurchaseData(productid);
    }
  }, []);

  const replaceCartItem = useCallback((productid, itemData) => {
    setItems((prev) =>
      prev.map((item) =>
        String(item.dtl?.[0]?.productid || item.id) === String(productid)
          ? { ...itemData, qty: Number(itemData.qty || item.qty) }
          : item,
      ),
    );
  }, []);

  const addToCart = useCallback((productid, qty, purchaseData = null) => {
    const nextQty = Number(qty) || 1;

    console.log(productid, nextQty);

    setProductIds((prev) =>
      prev.some((id) => String(id) === String(productid))
        ? prev
        : [...prev, productid],
    );

    setItems((prev) => {
      const exists = prev.some(
        (item) =>
          String(item.dtl?.[0]?.productid || item.id) === String(productid),
      );

      if (exists) {
        return prev.map((item) =>
          String(item.dtl?.[0]?.productid || item.id) === String(productid)
            ? { ...item, qty: nextQty }
            : item,
        );
      }

      return [...prev, { dtl: [{ productid }], qty: nextQty }];
    });

    setItemList((prev) => {
      const exists = prev.find((p) => String(p.productId) === String(productid));
      if (exists) {
        return prev.map((p) =>
          String(p.productId) === String(productid)
            ? { ...p, count: nextQty }
            : p,
        );
      }

      return [...prev, { productId: productid, count: nextQty }];
    });

    if (purchaseData) {
      saveCartPurchaseData({ ...purchaseData, quantity: nextQty });
    }

    fetchCart();
  }, [fetchCart]);

  const removeFromCart = useCallback((productid) => {
    const pid = String(productid);
    setItems((prev) =>
      prev.filter((item) => String(item.dtl?.[0]?.productid) !== pid),
    );
    setProductIds((prev) => prev.filter((id) => String(id) !== pid));
    setItemList((prev) => prev.filter((p) => String(p.productId) !== pid));
    removeSavedCartPurchaseData(productid);
  }, []);

  const deleteCartItems = useCallback(() => {
    setProductIds([]);
    setItems([]);
    setItemList([]);
    setCount(0);
    setWalletAmount("0.00");
    localStorage.removeItem("cartPurchaseOptions");
  }, []);

  const addCoupon = useCallback((couponid) => setAppliedCoupon(couponid), []);
  const removeCoupon = useCallback(() => setAppliedCoupon(null), []);

  const updateQty = useCallback((productId, minqty, type) => {
    setItemList((prev) =>
      prev
        .map((p) => {
          if (String(p.productId) !== String(productId)) return p;
          return {
            ...p,
            count: Math.max(
              0,
              p.count + (type === "plus" || type === "add" ? minqty : -minqty),
            ),
          };
        })
        .filter((p) => p.count > 0),
    );
  }, []);

  useEffect(() => {
    if (!items) return;
    const ids = items.map((item) => item.dtl?.[0]?.productid || item.id);
    setProductIds(ids);
    const itemCountMap = {};
    items.forEach((item) => {
      const id = item.dtl?.[0]?.productid || item.id;
      const qty = Number(item.qty) || 0;
      if (!id) return;
      itemCountMap[id] = (itemCountMap[id] || 0) + qty;
    });
    setItemList(
      Object.keys(itemCountMap).map((key) => ({
        productId: key,
        count: itemCountMap[key],
      })),
    );
  }, [items]);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const response = await getCartCount();
        setCount(response.count);
      } catch (err) {
        console.log(err);
      }
    };
    fetchCount();
  }, [items, productIds, itemList, userId]);

  const value = useMemo(
    () => ({
      count,
      items,
      productIds,
      itemList,
      appliedCoupon,
      userId,
      walletAmount,
      setUserId,
      addToCart,
      removeFromCart,
      deleteCartItems,
      addCoupon,
      removeCoupon,
      updateQty,
      setCartQty,
      replaceCartItem,
      fetchCart,
    }),
    [
      count,
      items,
      productIds,
      itemList,
      appliedCoupon,
      userId,
      walletAmount,
      addToCart,
      removeFromCart,
      deleteCartItems,
      addCoupon,
      removeCoupon,
      updateQty,
      setCartQty,
      replaceCartItem,
      fetchCart,
    ],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
